import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext'; 
import { useTenant } from '../../context/TenantContext'; // Added for Audit isolation
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { fetchAuditLogs } from '../../services/auditService'; // The new service

export default function Settings() {
  const [activeTab, setActiveTab] = useState('audit'); // Defaulting to the new tab for you to test
  const { currentUser } = useAuth();
  const { tenantId } = useTenant();
  
  // Google Calendar Integration State
  const [isConnectingCalendar, setIsConnectingCalendar] = useState(false);
  const [calendarData, setCalendarData] = useState({ connected: false, email: '' });
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);

  // Forensic Audit State
  const [auditLogs, setAuditLogs] = useState([]);
  const [isFetchingAudit, setIsFetchingAudit] = useState(false);
  const [auditFilters, setAuditFilters] = useState({ entityType: '', actionCategory: '', searchActor: '' });
  const [selectedDelta, setSelectedDelta] = useState(null);

  // --- Fetch Profile & Calendar Status ---
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!currentUser?.uid) return;
      try {
        const db = getFirestore();
        const userRef = doc(db, 'users', currentUser.uid);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists()) {
          const data = userSnap.data();
          if (data.googleCalendar?.connected) {
            setCalendarData({
              connected: true,
              email: data.googleCalendar.email || 'Connected Account'
            });
          }
        }
      } catch (error) {
        console.error("Failed to load user settings:", error);
      } finally {
        setIsLoadingProfile(false);
      }
    };

    fetchUserProfile();
  }, [currentUser]);

  // --- Fetch Immutable Audit Logs ---
  useEffect(() => {
    const loadAuditLedger = async () => {
      if (activeTab !== 'audit' || !tenantId) return;
      setIsFetchingAudit(true);
      try {
        const logs = await fetchAuditLogs(tenantId, auditFilters);
        // Client-side actor search filter for quick typing
        const filteredLogs = auditFilters.searchActor 
          ? logs.filter(log => log.actor?.email?.toLowerCase().includes(auditFilters.searchActor.toLowerCase()))
          : logs;
        setAuditLogs(filteredLogs);
      } catch (error) {
        console.error("Failed to load audit logs:", error);
      } finally {
        setIsFetchingAudit(false);
      }
    };
    loadAuditLedger();
  }, [activeTab, tenantId, auditFilters.entityType, auditFilters.actionCategory]);

  const handleConnectCalendar = async () => {
    setIsConnectingCalendar(true);
    try {
      const functions = getFunctions();
      const getGoogleAuthUrl = httpsCallable(functions, 'getGoogleAuthUrl');
      const currentRedirectUri = `${window.location.origin}/integrations/google/callback`;
      const response = await getGoogleAuthUrl({ redirectUri: currentRedirectUri });
      
      if (response.data && response.data.authUrl) {
        window.location.href = response.data.authUrl;
      }
    } catch (error) {
      console.error("Failed to fetch Google Auth URL:", error);
      alert("Failed to initialize Google connection. Check console.");
      setIsConnectingCalendar(false);
    }
  };

  const tabs = [
    { id: 'profile', label: 'My Profile', icon: 'fa-user' },
    { id: 'integrations', label: 'Integrations', icon: 'fa-plug' },
    { id: 'team', label: 'Team & Roles', icon: 'fa-users' },
    { id: 'billing', label: 'Billing & Plans', icon: 'fa-credit-card' },
    { id: 'audit', label: 'Security & Audit', icon: 'fa-shield-halved' },
  ];

  return (
    <div className="animate-in fade-in duration-500 h-[calc(100vh-120px)] flex flex-col">
      <div className="mb-8 shrink-0">
        <h1 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight uppercase">System Settings</h1>
        <p className="text-slate-500 dark:text-slate-400 text-sm mt-1 font-medium">Manage your personal profile, enterprise integrations, and compliance.</p>
      </div>

      <div className="flex flex-1 gap-8 min-h-0">
        
        {/* SIDEBAR NAVIGATION */}
        <div className="w-64 flex flex-col gap-2 shrink-0">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center justify-between px-4 py-3 text-xs font-bold uppercase tracking-widest transition-colors border-l-2 ${
                activeTab === tab.id 
                  ? 'bg-white dark:bg-slate-800 border-emerald-500 text-emerald-700 dark:text-emerald-400 shadow-sm' 
                  : 'border-transparent text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800/50 hover:text-slate-800 dark:hover:text-slate-200'
              }`}
            >
              <div className="flex items-center gap-3">
                <i className={`fa-solid ${tab.icon} w-4 text-center`}></i>
                {tab.label}
              </div>
              {tab.id === 'audit' && <i className="fa-solid fa-lock text-[10px] opacity-50"></i>}
            </button>
          ))}
        </div>

        {/* CONTENT AREA */}
        <div className="flex-1 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 shadow-sm overflow-hidden flex flex-col">
          
          {/* INTEGRATIONS TAB */}
          {activeTab === 'integrations' && (
            <div className="max-w-3xl animate-in fade-in p-8 overflow-y-auto custom-scrollbar">
              <h2 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-widest border-b border-slate-200 dark:border-slate-700 pb-4 mb-6">
                Connected Applications
              </h2>

              <div className="bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 p-6 flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-full flex items-center justify-center shrink-0 shadow-sm">
                    <img src="https://upload.wikimedia.org/wikipedia/commons/a/a5/Google_Calendar_icon_%282020%29.svg" alt="Google Calendar" className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-sm font-black text-slate-900 dark:text-white">Google Calendar Sync</h3>
                    <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mt-1 max-w-md">
                      Automatically sync scheduled CRM pipeline leads directly to your personal or work Google Calendar. Invites will be sent to clients from your email address.
                    </p>
                    
                    {!isLoadingProfile && calendarData.connected && (
                      <div className="mt-3 flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                        <span className="text-[10px] font-bold text-emerald-700 dark:text-emerald-400 uppercase tracking-widest">
                          Active • {calendarData.email}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="shrink-0">
                  {isLoadingProfile ? (
                    <div className="w-32 h-10 bg-slate-200 dark:bg-slate-700 animate-pulse"></div>
                  ) : calendarData.connected ? (
                    <button className="bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-[10px] font-bold px-6 py-3 uppercase tracking-widest cursor-not-allowed">
                      Connected
                    </button>
                  ) : (
                    <button 
                      onClick={handleConnectCalendar}
                      disabled={isConnectingCalendar}
                      className="bg-slate-900 dark:bg-white text-white dark:text-slate-900 hover:bg-slate-800 dark:hover:bg-slate-100 text-[10px] font-bold px-6 py-3 uppercase tracking-widest transition-colors shadow-sm disabled:opacity-70 flex items-center gap-2"
                    >
                      {isConnectingCalendar ? <i className="fa-solid fa-spinner fa-spin"></i> : <i className="fa-brands fa-google"></i>}
                      {isConnectingCalendar ? 'Connecting...' : 'Connect Account'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* SECURITY & AUDIT TAB (NEW) */}
          {activeTab === 'audit' && (
            <div className="flex flex-col h-full animate-in fade-in">
              <div className="p-6 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 shrink-0">
                <div className="flex justify-between items-end mb-4">
                  <div>
                    <h2 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-widest">
                      Forensic Audit Ledger
                    </h2>
                    <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mt-1">
                      Immutable, cryptographically signed ledger of critical platform mutations.
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[9px] font-black text-emerald-600 uppercase tracking-widest bg-emerald-50 border border-emerald-200 px-2 py-1 rounded-sm">
                      <i className="fa-solid fa-shield-check"></i> Tamper-Evident Active
                    </span>
                  </div>
                </div>

                {/* Smart Faceted Filters */}
                <div className="flex gap-4 items-center mt-6">
                  <select 
                    value={auditFilters.actionCategory}
                    onChange={(e) => setAuditFilters({...auditFilters, actionCategory: e.target.value})}
                    className="p-2 text-[10px] font-bold uppercase tracking-widest bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-sm focus:outline-none focus:border-emerald-500 text-slate-700 dark:text-slate-200 cursor-pointer shadow-sm"
                  >
                    <option value="">All Actions</option>
                    <option value="RULE_MUTATION">Commission Matrices</option>
                    <option value="CLEARANCE_CHANGE">Role & Clearance</option>
                    <option value="DOOR_GOVERNANCE">Door Mints</option>
                    <option value="PAYROLL_STAGED">Payroll Execution</option>
                  </select>

                  <select 
                    value={auditFilters.entityType}
                    onChange={(e) => setAuditFilters({...auditFilters, entityType: e.target.value})}
                    className="p-2 text-[10px] font-bold uppercase tracking-widest bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-sm focus:outline-none focus:border-emerald-500 text-slate-700 dark:text-slate-200 cursor-pointer shadow-sm"
                  >
                    <option value="">All Entities</option>
                    <option value="MASTER_RULE">Global Rules</option>
                    <option value="CONTRACTOR">1099 Operators</option>
                    <option value="DOOR">Facilities</option>
                  </select>

                  <div className="relative flex-1 max-w-xs">
                    <i className="fa-solid fa-search absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[10px]"></i>
                    <input 
                      type="text" 
                      placeholder="Search by Actor Email..." 
                      value={auditFilters.searchActor}
                      onChange={(e) => setAuditFilters({...auditFilters, searchActor: e.target.value})}
                      className="w-full pl-8 pr-3 py-2 text-[10px] font-bold bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-sm focus:outline-none focus:border-emerald-500 text-slate-700 dark:text-slate-200 placeholder:text-slate-400 uppercase tracking-widest shadow-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Data Grid */}
              <div className="flex-1 overflow-y-auto custom-scrollbar bg-white dark:bg-slate-800">
                {isFetchingAudit ? (
                  <div className="flex justify-center items-center h-32">
                    <i className="fa-solid fa-spinner fa-spin text-slate-400 text-2xl"></i>
                  </div>
                ) : auditLogs.length === 0 ? (
                  <div className="p-12 text-center text-slate-400 dark:text-slate-500 text-[10px] font-bold uppercase tracking-widest">
                    <i className="fa-solid fa-file-shield text-3xl mb-3 block opacity-50"></i>
                    No matching audit records found.
                  </div>
                ) : (
                  <table className="w-full text-left border-collapse">
                    <thead className="bg-slate-50 dark:bg-slate-900/80 sticky top-0 border-b border-slate-200 dark:border-slate-700 z-10">
                      <tr>
                        <th className="px-6 py-3 text-[9px] font-black text-slate-500 uppercase tracking-widest">Timestamp (UTC)</th>
                        <th className="px-6 py-3 text-[9px] font-black text-slate-500 uppercase tracking-widest">Actor</th>
                        <th className="px-6 py-3 text-[9px] font-black text-slate-500 uppercase tracking-widest">Action</th>
                        <th className="px-6 py-3 text-[9px] font-black text-slate-500 uppercase tracking-widest">Target Entity</th>
                        <th className="px-6 py-3 text-[9px] font-black text-slate-500 uppercase tracking-widest text-right">Verification</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50">
                      {auditLogs.map((log) => (
                        <tr key={log.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                          <td className="px-6 py-4 text-[10px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-widest">
                            {log.timestamp?.toDate ? log.timestamp.toDate().toLocaleString() : 'Just Now'}
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-xs font-bold text-slate-900 dark:text-white">{log.actor?.email || 'SYSTEM'}</div>
                            <div className="text-[9px] font-mono text-slate-400 mt-0.5">{log.actor?.role || 'INTERNAL'}</div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-[9px] font-black px-2.5 py-1 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 uppercase tracking-widest rounded-sm border border-slate-200 dark:border-slate-600">
                              {log.category?.replace('_', ' ')}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-xs font-bold text-slate-800 dark:text-slate-200">{log.target?.entityName || 'Global Fleet'}</div>
                            <div className="text-[8px] font-bold uppercase tracking-widest text-slate-400 mt-0.5">{log.target?.entityType} • {log.target?.entityId}</div>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <button 
                              onClick={() => setSelectedDelta(log)}
                              className="bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 hover:border-indigo-500 text-indigo-600 dark:text-indigo-400 text-[9px] font-black uppercase tracking-widest px-3 py-1.5 transition-colors rounded-sm shadow-sm"
                            >
                              View Delta
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}

          {/* PLACEHOLDER TABS */}
          {(activeTab === 'profile' || activeTab === 'team' || activeTab === 'billing') && (
            <div className="p-8 animate-in fade-in">
              <h2 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-widest border-b border-slate-200 dark:border-slate-700 pb-4 mb-6">
                {tabs.find(t => t.id === activeTab)?.label}
              </h2>
              <div className="text-center py-12 text-slate-400 dark:text-slate-500 border-2 border-dashed border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-900/20">
                <i className="fa-solid fa-lock text-3xl mb-3 opacity-50 block"></i>
                <p className="text-[10px] font-bold uppercase tracking-widest">Enterprise Module Locked</p>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* DELTA VIEWER MODAL */}
      {selectedDelta && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/80 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col overflow-hidden animate-in zoom-in-95">
            
            <div className="bg-slate-100 dark:bg-slate-800 px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center shrink-0">
              <div>
                <h3 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-widest flex items-center gap-2">
                  <i className="fa-solid fa-file-code"></i> Data Mutation Delta
                </h3>
                <p className="text-[9px] font-mono text-slate-500 mt-1 uppercase">Log ID: {selectedDelta.id}</p>
              </div>
              <button onClick={() => setSelectedDelta(null)} className="text-slate-400 hover:text-slate-800 dark:hover:text-white transition-colors">
                <i className="fa-solid fa-xmark text-xl"></i>
              </button>
            </div>

            <div className="p-6 flex-1 overflow-y-auto custom-scrollbar flex gap-6">
              <div className="flex-1 flex flex-col">
                <div className="text-[10px] font-black text-red-600 bg-red-50 border border-red-200 px-3 py-2 uppercase tracking-widest flex items-center justify-between rounded-t-sm">
                  <span>Previous State</span>
                  <i className="fa-solid fa-minus"></i>
                </div>
                <div className="flex-1 bg-slate-900 p-4 rounded-b-sm border-x border-b border-slate-800 overflow-x-auto">
                  <pre className="text-xs font-mono text-slate-300 leading-relaxed">
                    {JSON.stringify(selectedDelta.stateDiff?.before || {}, null, 2)}
                  </pre>
                </div>
              </div>
              
              <div className="flex-1 flex flex-col">
                <div className="text-[10px] font-black text-emerald-600 bg-emerald-50 border border-emerald-200 px-3 py-2 uppercase tracking-widest flex items-center justify-between rounded-t-sm">
                  <span>New State</span>
                  <i className="fa-solid fa-plus"></i>
                </div>
                <div className="flex-1 bg-slate-900 p-4 rounded-b-sm border-x border-b border-slate-800 overflow-x-auto">
                  <pre className="text-xs font-mono text-emerald-400 leading-relaxed">
                    {JSON.stringify(selectedDelta.stateDiff?.after || {}, null, 2)}
                  </pre>
                </div>
              </div>
            </div>

            <div className="bg-slate-50 dark:bg-slate-900 px-6 py-4 border-t border-slate-200 dark:border-slate-700 shrink-0">
              <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1">Cryptographic Integrity Signature (SHA-256)</p>
              <p className="text-xs font-mono text-slate-800 dark:text-slate-300 truncate opacity-70">
                {selectedDelta.signature || 'N/A'}
              </p>
            </div>

          </div>
        </div>
      )}

    </div>
  );
}